/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { getUserTimezone } from '../utils/dateFormat';

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
  formatDate: (date: Date | string | null | undefined) => string;
  formatDateTime: (date: Date | string | null | undefined) => string;
  formatTime: (date: Date | string | null | undefined) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency?: string) => string;
}

const LOCALE_STORAGE_KEY = 'user-locale';
const DEFAULT_LOCALE = 'en-US';

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => {
    return localStorage.getItem(LOCALE_STORAGE_KEY) || DEFAULT_LOCALE;
  });

  const setLocale = (newLocale: string) => {
    setLocaleState(newLocale);
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
  };

  const value = useMemo<LocaleContextValue>(() => {
    const timezone = getUserTimezone();

    const parseDate = (date: Date | string | null | undefined): Date | null => {
      if (!date) return null;
      if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;

      const str =
        date.endsWith('Z') || date.includes('+') || /T\d{2}:\d{2}.*[+-]\d{2}/.test(date)
          ? date
          : `${date}Z`;
      const parsed = new Date(str);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    return {
      locale,
      setLocale,
      formatDate: (date) => {
        const d = parseDate(date);
        if (!d) return '—';
        return d.toLocaleDateString(locale, { timeZone: timezone });
      },
      formatDateTime: (date) => {
        const d = parseDate(date);
        if (!d) return '—';
        return d.toLocaleString(locale, {
          timeZone: timezone,
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
      formatTime: (date) => {
        const d = parseDate(date);
        if (!d) return '—';
        return d.toLocaleTimeString(locale, {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
      formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
      formatCurrency: (value, currency = 'USD') =>
        new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value),
    };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }

  return context;
}
